"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import Link from "next/link";
import { Switch } from "./ui/switch";
import { useMounted } from "@/hooks/use-mounted";
export default function CookieBanner() {
  const $ = _c(13);
  const mounted = useMounted();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      essential: true,
      analytics: false,
      marketing: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [consent, setConsent] = useState(t0);
  const applyConsentGTM = _temp;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const storedConsent = localStorage.getItem("spoclearn-cookie-consent");
      if (!storedConsent) {
        const timer = setTimeout(() => {
          setVisible(true);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        ;
        try {
          const parsed = JSON.parse(storedConsent);
          applyConsentGTM(parsed.analytics, parsed.marketing);
        } catch (t3) {
          const e = t3;
          console.error("Error parsing cookie consent", e);
        }
      }
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const preferences = {
        analytics: true,
        marketing: true
      };
      localStorage.setItem("spoclearn-cookie-consent", JSON.stringify(preferences));
      applyConsentGTM(true, true);
      setVisible(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleAcceptAll = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const preferences_0 = {
        analytics: false,
        marketing: false
      };
      localStorage.setItem("spoclearn-cookie-consent", JSON.stringify(preferences_0));
      applyConsentGTM(false, false);
      setVisible(false);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleRejectAll = t4;
  let t5;
  if ($[5] !== consent) {
    t5 = () => {
      localStorage.setItem("spoclearn-cookie-consent", JSON.stringify(consent));
      applyConsentGTM(consent.analytics, consent.marketing);
      setVisible(false);
      setCustomizing(false);
    };
    $[5] = consent;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleSaveCustom = t5;
  if (!mounted || !visible) {
    return null;
  }
  let t6;
  if ($[7] !== consent.analytics || $[8] !== consent.essential || $[9] !== consent.marketing || $[10] !== customizing || $[11] !== handleSaveCustom) {
    t6 = <Card className="bg-card text-card-foreground fixed bottom-6 left-6 z-50 w-full max-w-[calc(100vw-2rem)] shadow-2xl transition-all duration-300 sm:max-w-md">{!customizing ? <><CardHeader className="pb-3"><div className="flex items-center gap-2"><div className="bg-primary/10 text-primary shrink-0 rounded-lg p-1.5"><Cookie className="size-5" /></div><CardTitle className="text-base font-semibold">Cookie Settings</CardTitle></div><CardDescription className="mt-1.5 text-xs leading-relaxed">Spoclearn uses cookies to make sure our website works as expected, to understand how it is used, and to deliver personalized experiences. You can accept all cookies or review your choices. Learn more in our{" "}<Link href="/privacy-policy/" className="text-primary decoration-primary/30 font-semibold underline underline-offset-4 transition">Privacy Policy</Link>.</CardDescription></CardHeader><CardFooter className="bg-card flex flex-wrap justify-end gap-2 border-0 px-4 py-3"><Button variant="outline" size="sm" onClick={() => setCustomizing(true)}>Customize</Button><Button variant="outline" size="sm" onClick={handleRejectAll}>Reject All</Button><Button variant="default" size="sm" onClick={handleAcceptAll}>Accept All</Button></CardFooter></> : <><CardHeader className="border-b pb-3"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold tracking-wider uppercase">Customize Preferences</CardTitle><Button variant="ghost" size="icon-xs" onClick={() => setCustomizing(false)} className="text-muted-foreground hover:text-foreground rounded-full"><X className="size-4" /></Button></div></CardHeader><CardContent className="space-y-4 py-4"><div className="flex items-start justify-between gap-4 border-b pb-2"><div className="space-y-0.5"><Label htmlFor="essential-cookies" className="text-sm font-semibold">Essential Cookies</Label><p className="text-muted-foreground text-[11px] leading-normal">Required for core functionality such as secure logins, shopping cart preferences, and loading the site correctly.</p></div><Switch id="essential-cookies" checked={consent.essential} disabled={true} onCheckedChange={checked => setConsent(prev => ({
              ...prev,
              analytics: !!checked
            }))} size="sm" /></div><div className="flex items-start justify-between gap-4 border-b pb-2"><div className="space-y-0.5"><Label htmlFor="analytics-cookies" className="text-sm font-semibold">Analytics Cookies</Label><p className="text-muted-foreground text-[11px] leading-normal">Help us understand how visitors use Spoclearn so we can measure what works well and improve future experiences.</p></div><Switch id="analytics-cookies" checked={consent.analytics} onCheckedChange={checked_0 => setConsent(prev_0 => ({
              ...prev_0,
              analytics: !!checked_0
            }))} size="sm" /></div><div className="flex items-start justify-between gap-4 pb-1"><div className="space-y-0.5"><Label htmlFor="marketing-cookies" className="text-sm font-semibold">Marketing Cookies</Label><p className="text-muted-foreground text-[11px] leading-normal">Allow us to offer tailored content, relevant recommendations, and promotions across Spoclearn channels.</p></div><Switch id="marketing-cookies" checked={consent.marketing} onCheckedChange={checked_1 => setConsent(prev_1 => ({
              ...prev_1,
              marketing: !!checked_1
            }))} size="sm" /></div></CardContent><CardFooter className="bg-muted/50 flex items-center justify-between border-t px-4 py-3"><Button variant="link" size="xs" onClick={() => setCustomizing(false)} className="text-muted-foreground hover:text-foreground h-auto p-0">Back</Button><Button variant="default" size="sm" onClick={handleSaveCustom}>Save Preferences</Button></CardFooter></>}</Card>;
    $[7] = consent.analytics;
    $[8] = consent.essential;
    $[9] = consent.marketing;
    $[10] = customizing;
    $[11] = handleSaveCustom;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function _temp(analytics, marketing) {
  if (typeof window !== "undefined") {
    const dataLayer = (window as any).dataLayer || [];
    dataLayer.push({
      event: "cookie_consent_update",
      consent_analytics: analytics ? "granted" : "denied",
      consent_marketing: marketing ? "granted" : "denied"
    });
    (window as any).dataLayer = dataLayer;
  }
}